import { QueryScopeConfigId } from '@principle-theorem/principle-core/interfaces';
import {
  BuiltQueryScope,
  IBaseMeasures,
  IQueryScope,
  IReportingQuery,
  IScopedQuery,
  ITableJoin,
} from '@principle-theorem/reporting/interfaces';
import { flatMap, uniq } from 'lodash';

export class QueryScopeBuilder {
  constructor(public factTable: IBaseMeasures) {}

  buildScopedQuery(query: IReportingQuery, scope: IQueryScope): IScopedQuery {
    if (!scope.bigqueryDataset) {
      throw new Error(
        `Query scope for ${scope.orgRef.path} is missing bigqueryDataset. ` +
          `The caller must resolve it from the organisation before building the query.`
      );
    }
    const scopeQuery = this.buildQueryScope(scope);
    return {
      dataSet: scope.bigqueryDataset,
      query: {
        ...query,
        attributes: [...scopeQuery.attributes, ...query.attributes],
        filters: [...scopeQuery.filters, ...(query.filters ?? [])],
        joins: this.mergeTableJoins(scopeQuery.joins, query.joins),
      },
    };
  }

  hasScope(id: string): boolean {
    return this.factTable.scopes.length &&
      this.factTable.scopes.some((scope) => scope.id === id)
      ? true
      : false;
  }

  buildQueryScope(scope: IQueryScope): BuiltQueryScope {
    const queries = this.factTable.scopes.map((config) => {
      const scopeRequest = scope.scopeRequests[config.id as QueryScopeConfigId];
      if (!config.isValid(scopeRequest)) {
        throw new Error(`Invalid scope given for scope config: ${config.id}`);
      }
      return config.buildQuery(scopeRequest, { factTable: this.factTable });
    });
    return {
      attributes: uniq(flatMap(queries, (query) => query.attributes)),
      filters: uniq(flatMap(queries, (query) => query.filters)),
      joins: uniq(flatMap(queries, (query) => query.joins)),
    };
  }

  mergeTableJoins(
    scopeJoins: ITableJoin[],
    queryJoins: ITableJoin[] = []
  ): ITableJoin[] {
    return [scopeJoins, queryJoins]
      .flat()
      .reduce((joins: ITableJoin[], join: ITableJoin) => {
        const aliasConflict = joins.find((j) => j.alias === join.alias);
        if (!aliasConflict) {
          return [...joins, join];
        }
        const isSameJoin =
          aliasConflict.table === join.table &&
          aliasConflict.sourceJoinKey === join.sourceJoinKey &&
          aliasConflict.destinationJoinKey === join.destinationJoinKey;
        if (!isSameJoin) {
          throw new Error(
            `Table join conflict detected for alias: ${join.alias}`
          );
        }
        return joins;
      }, []);
  }
}
